import { Check, Star } from "lucide-react";
import { getNextCycleVoteValue } from "../utils/calendar-month-vote-controls.js";

// WCAG 2.5.5 touch targets (Batch 6d-2). The vote dots are 24px (the AA
// minimum) and sit in dense calendar tiles, so we grow the *tap* area with an
// invisible ::before rather than the visible box (which would reflow the tile
// or overlap neighbours). Two constraints shape it:
//   • WIDTH — the adjacent feasible/preferred dots are only 4px apart, so the
//     pair keeps before:w-6 (== the 24px box) so the two hit-areas never
//     overlap and one dot can't steal the other's taps. The isolated cycle
//     button has no neighbour, so it takes the full before:w-11.
//   • VERTICAL ANCHOR — the dots render flush against the TOP of the RBC tile,
//     whose `.rbc-event-content` is `overflow:hidden`, so a vertically-CENTERED
//     ::before has its upper half clipped (realised target ~34px, manual-verify
//     FAIL). We anchor the ::before to the dot's top (before:top-0) and grow it
//     DOWNWARD into the tile body where there is room → a genuine 44px-tall
//     target. The rows below (count text + SlotIndicator) paint later in the
//     DOM, so the downward area only captures the non-interactive count text;
//     the SlotIndicator keeps its own tap region.
//   • DISABLED — a disabled button still SWALLOWS pointer events over its box
//     (incl. its ::before) rather than passing them through. Clicking a slot
//     tile opens the vote dialog, so a disabled dot's downward ::before would
//     absorb that tile-open click (past-slot tiles never open). `disabled:`
//     hides the ::before so disabled dots revert to their 24px box and the
//     tile stays clickable; nothing to tap on a disabled control anyway.
const HIT_AREA_BASE =
  "relative before:absolute before:left-1/2 before:top-0 before:-translate-x-1/2 before:content-[''] disabled:before:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const HIT_AREA_VERTICAL = `${HIT_AREA_BASE} before:h-11 before:w-6`;
const HIT_AREA_FULL = `${HIT_AREA_BASE} before:h-11 before:w-11`;

/**
 * QuickVoteCluster — top-right ✓ Feasible / ⭐ Preferred toggle (or single
 * cycle button when a day has more than one slot).
 *
 * Shared between SchedulerPage's month-view EventCell and the week/day
 * SchedulerCalendarSlot. Owns the toggle/cycle math + RBC click swallowing
 * (onMouseDown stopPropagation + preventDefault wrapping onClick) so neither
 * caller has to redo it.
 *
 * Pure render component — no hooks, no context.
 *
 * Props:
 *  - slotId: string — used in testids and forwarded to onSetVote
 *  - vote: "FEASIBLE" | "PREFERRED" | null — current local vote
 *  - canInteract: boolean — gates buttons (false if isPast/isLocked/!canVote/noTimesWork)
 *  - hasMultipleSlots: boolean — when true, render a single 3-state cycle
 *    button instead of the feasible+preferred pair
 *  - ariaLabelTarget: string — human-readable label inserted into aria-labels
 *    (e.g., event.timeLabel for month, rangeLabel for week/day)
 *  - testIdPrefix: "month-slot" | "week-slot" | "day-slot" — testid namespace
 *  - onSetVote: (slotId, nextValue: "FEASIBLE"|"PREFERRED"|null) => void
 */
export function QuickVoteCluster({
  slotId,
  vote,
  canInteract,
  hasMultipleSlots,
  ariaLabelTarget,
  testIdPrefix,
  onSetVote,
}) {
  const preventCalendarSelect = (callback) => (clickEvent) => {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();
    callback();
  };

  const setFeasible = () => {
    if (!canInteract || vote === "PREFERRED") return;
    onSetVote(slotId, vote === "FEASIBLE" ? null : "FEASIBLE");
  };

  const setPreferred = () => {
    if (!canInteract) return;
    onSetVote(slotId, vote === "PREFERRED" ? null : "PREFERRED");
  };

  const cycleVote = () => {
    if (!canInteract) return;
    onSetVote(slotId, getNextCycleVoteValue(vote));
  };

  return (
    <div className="flex items-center gap-1">
      {hasMultipleSlots ? (
        <button
          type="button"
          data-testid={`${testIdPrefix}-cycle-${slotId}`}
          aria-label={`Cycle vote for ${ariaLabelTarget}`}
          aria-pressed={vote === "FEASIBLE" || vote === "PREFERRED"}
          disabled={!canInteract}
          onMouseDown={(clickEvent) => clickEvent.stopPropagation()}
          onClick={preventCalendarSelect(cycleVote)}
          className={`${HIT_AREA_FULL} inline-flex h-6 min-w-[2.15rem] items-center justify-center rounded-full border px-1.5 text-[10px] font-semibold transition ${
            vote === "PREFERRED"
              ? "border-green-200 bg-green-100 text-green-800"
              : vote === "FEASIBLE"
                ? "border-blue-200 bg-blue-100 text-blue-800"
                : "border-white/40 bg-white/15 text-white"
          } disabled:cursor-not-allowed disabled:opacity-40`}
        >
          {vote === "PREFERRED" ? (
            <Star size={12} className="fill-current" />
          ) : vote === "FEASIBLE" ? (
            <Check size={12} />
          ) : (
            "○"
          )}
        </button>
      ) : (
        <>
          <button
            type="button"
            data-testid={`${testIdPrefix}-feasible-${slotId}`}
            aria-label={`Toggle feasible vote for ${ariaLabelTarget}`}
            aria-pressed={vote === "FEASIBLE" || vote === "PREFERRED"}
            disabled={!canInteract || vote === "PREFERRED"}
            onMouseDown={(clickEvent) => clickEvent.stopPropagation()}
            onClick={preventCalendarSelect(setFeasible)}
            className={`${HIT_AREA_VERTICAL} inline-flex h-6 w-6 items-center justify-center rounded-full border text-[10px] transition ${
              vote === "FEASIBLE" || vote === "PREFERRED"
                ? "border-blue-200 bg-blue-100 text-blue-800"
                : "border-white/40 bg-white/15 text-white"
            } disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <Check size={12} />
          </button>
          <button
            type="button"
            data-testid={`${testIdPrefix}-preferred-${slotId}`}
            aria-label={`Toggle preferred vote for ${ariaLabelTarget}`}
            aria-pressed={vote === "PREFERRED"}
            disabled={!canInteract}
            onMouseDown={(clickEvent) => clickEvent.stopPropagation()}
            onClick={preventCalendarSelect(setPreferred)}
            className={`${HIT_AREA_VERTICAL} inline-flex h-6 w-6 items-center justify-center rounded-full border text-[10px] transition ${
              vote === "PREFERRED"
                ? "border-green-200 bg-green-100 text-green-800"
                : "border-white/40 bg-white/15 text-white"
            } disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <Star size={12} className={vote === "PREFERRED" ? "fill-current" : ""} />
          </button>
        </>
      )}
    </div>
  );
}
